import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth, useCreateTestPlan } from '@/hooks'
import { Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

interface CreateTestPlanDialogProps {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateTestPlanDialog({ projectId, open, onOpenChange }: CreateTestPlanDialogProps) {
  const [title, setTitle] = useState('')
  const { user } = useAuth()
  const createTestPlan = useCreateTestPlan(projectId)

  useEffect(() => {
    if (!open) {
      setTitle('')
    }
  }, [open])

  const handleCreate = () => {
    if (!title.trim()) return

    createTestPlan.mutate(
      {
        title: title.trim(),
      },
      {
        onSuccess: () => {
          setTitle('')
          onOpenChange(false)
        },
        onError: () => {
          toast.error('Failed to create test plan')
        },
      },
    )
  }

  const handleCancel = () => {
    setTitle('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[984px]">
        <DialogHeader>
          <DialogTitle>Create New Test Plan</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="test-plan-title">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="test-plan-title"
              placeholder="e.g. Login Module – Smoke Test"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate()
              }}
              data-testid="test-plan-title-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Owner</Label>
            <Input
              value={user?.displayName ?? ''}
              disabled
              data-testid="test-plan-user-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!title.trim() || createTestPlan.isPending}
            data-testid="create-test-plan-submit"
          >
            <Check className="h-4 w-4" />
            {createTestPlan.isPending ? 'Creating...' : 'Create Test Plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
